import gsap from 'gsap/all';

import { KHPoint } from '../../../../../LearningWorlds/pixi-puzzle-framework/PuzzleEngine/KHPoint';
import { applyFrameToElement, MANIP_FRAME_ACTIVE, ManipFrame, parseFrameString } from '../../../../../utils/utils';
import { SetDraggable } from '../../../../mixins/DraggableMixin';

import { ScorableDropRegion } from './ScorabelDropRegion';

export type DragRegionConfig = {
  parent: HTMLElement;
  frame?: string;
  src?: string;
  visualElement: HTMLImageElement;
  key: string;
  createIndex: number;
  dropRegions: ScorableDropRegion[];
  makeclones: boolean;
  hasoutline?: boolean;
  returnunregistered?: boolean;
  centerx?: boolean;
  centery?: boolean;
  answervalue?: string;
};

export class ScorableDragRegion {
  public dragElement: HTMLDivElement;
  public config: DragRegionConfig;
  public isPositioned = false;
  public readyClone: ScorableDragRegion | null = null;
  public onResetCallback: (() => void) | null = null;
    public currentRegion: ScorableDropRegion | null = null;

  private initialFrame: ManipFrame = { height: 0, width: 0, x: 100, y: 100 };
  private hoverRegion: ScorableDropRegion | null = null;
  private cloneParent?: ScorableDragRegion;
  private draggableMixin: (() => void) | undefined;

  public constructor(config: DragRegionConfig) {
    this.config = config;
    this.dragElement = document.createElement('div');
    config.parent.appendChild(this.dragElement);

    this.setupImage();

    this.initialFrame.width = this.config.visualElement.naturalWidth;
    this.initialFrame.height = this.config.visualElement.naturalHeight;

    if (this.config.frame) {
      this.isPositioned = true;
      this.initialFrame = parseFrameString(this.config.frame);

      // the draggable can't be smaller than its visual element
      this.initialFrame.width = Math.max(this.initialFrame.width, this.config.visualElement.naturalWidth);
      this.initialFrame.height = Math.max(this.initialFrame.height, this.config.visualElement.naturalHeight);
    }

    if (this.dragElement) {
      if (this.config.centerx) {
        this.initialFrame.x -= this.initialFrame.width / 2;
      }

      if (this.config.centery) {
        this.initialFrame.y -= this.initialFrame.height / 2;
      }

      applyFrameToElement(this.dragElement, this.initialFrame);
    }

    this.setupDrag();

    if (this.config.makeclones) {
      this.makeClone();
    }
  }

  public updateAnchorPosition(x: number, y: number, cloneOnly = false): void {
    if (this.readyClone) {
      this.readyClone.updateAnchorPosition(x, y);

      if (cloneOnly) {
        return;
      }
    }

    this.initialFrame.x = x;
    this.initialFrame.y = y;

    this.dragElement.style.left = `${x}px`;
    this.dragElement.style.top = `${y}px`;
  }

  public getPosition(): KHPoint {
    return { x: parseFloat(this.dragElement.style.left), y: parseFloat(this.dragElement.style.top) };
  }

  public getUnscaledSize(): ManipFrame {
    return { x: 0, y: 0, width: this.initialFrame.width, height: this.initialFrame.height };
  }

  private setupDrag(): void {
    if (this.dragElement) {
      // if you're the thing that makes clones you don't actually drag at all
      if (!this.config.makeclones) {
        this.draggableMixin = SetDraggable(this.dragElement, this.startDrag.bind(this), this.onDrag.bind(this), this.finishDrag.bind(this));

        // this should eat any click events that happen on the drag element
        this.dragElement.addEventListener('click', e => {
          e.stopPropagation();
        });
      }
    }
  }

  private setupImage(): void {
    if (this.dragElement) {
      this.dragElement.appendChild(this.config.visualElement);

      if (this.config.hasoutline) {
        this.dragElement.style.border = MANIP_FRAME_ACTIVE;
        this.dragElement.style.borderRadius = '10%';
      }
    }

    this.dragElement.style.cursor = 'grab';
  }

  private startDrag(point: KHPoint, e: MouseEvent | TouchEvent): void {
    this.resetDragRegion();
    this.dragElement.style.cursor = 'grabbing';
    this.dragElement.style.zIndex = '1000';
  }

  private onDrag(point: KHPoint): void {
    const overlapRegion = this.findOverlapRegion();
    this.hoverRegion?.setIsHoverTarget(false);
    overlapRegion?.setIsHoverTarget(true);
    this.hoverRegion = overlapRegion;
  }

  public nearestSnapPoint(overlapRegion: ScorableDropRegion): KHPoint {
    const dragRect = this.dragElement.getBoundingClientRect();
    const regionRect = overlapRegion.getRect();

    const offset = this.findRectangleOffset(dragRect, regionRect);

    return { x: parseFloat(this.dragElement.style.left) + offset.x, y: parseFloat(this.dragElement.style.top) + offset.y };
  }

  private finishDrag(point: KHPoint, e: MouseEvent | TouchEvent): void {
    const overlapRegion = this.findOverlapRegion();
    this.dragElement.style.cursor = 'grabbing';

    if (overlapRegion?.isValidDrop()) {
      overlapRegion.setIsHoverTarget(false);
      this.hoverRegion = null;
      this.currentRegion = overlapRegion;
      const pos = overlapRegion.getCenterPosition();

      // in single mode snap the draggable to middle region
      if (overlapRegion.isSingle()) {
        const middleX = pos.x - (this.dragElement.clientWidth / 2);
        const middleY = pos.y - (this.dragElement.clientHeight / 2);

        this.dragElement.style.left = `${middleX}px`;
        this.dragElement.style.top = `${middleY}px`;
      } else {
        // we should find the closest point where the entire draggable will fit in the region
        const nearestPoint = this.nearestSnapPoint(overlapRegion);
        this.dragElement.style.left = `${nearestPoint.x.toString()}px`;
        this.dragElement.style.top = `${nearestPoint.y.toString()}px`;
      }

      this.currentRegion.addOccupiedRegion(this);
    } else {
      if (this.config.returnunregistered || this.cloneParent) {
        this.resetDragRegion();
        this.resetPosition();
      }
    }

    if (this.cloneParent && this.cloneParent.readyClone === this) {
      this.cloneParent.readyClone = null;
      this.cloneParent.makeClone();
    }
  }

  private findOverlapRegion(): ScorableDropRegion | null {
    const dragRect = this.dragElement.getBoundingClientRect();
    let foundRegion: ScorableDropRegion | null = null;
    let biggestArea = 0;

    for (const region of this.config.dropRegions) {
      const rect = region.getRect();
      if (this.findArea(dragRect, rect) > biggestArea) {
        biggestArea = this.findArea(dragRect, rect);
        foundRegion = region;
      }
    }

    return foundRegion;
  }

  /**
   * Calculates the area of overlap between two DOMRects.
   * @param rect1 The first DOMRect.
   * @param rect2 The second DOMRect.
   * @returns The area of overlap between the two rectangles. Returns 0 if they don't overlap.
   */
  private findArea(rect1: DOMRect, rect2: DOMRect): number {
    // Calculate the (x, y) coordinates of the intersection rectangle's top-left and bottom-right corners
    const xOverlap = Math.max(0, Math.min(rect1.right, rect2.right) - Math.max(rect1.left, rect2.left));
    const yOverlap = Math.max(0, Math.min(rect1.bottom, rect2.bottom) - Math.max(rect1.top, rect2.top));

    // If there's no overlap in either dimension, the overlap area is 0
    if (xOverlap === 0 || yOverlap === 0) {
      return 0;
    }

    // Otherwise, the overlap area is the product of the overlaps in the x and y dimensions
    return xOverlap * yOverlap;
  }

  /**
   * Offset required to try and fit rect1 inside rect2
   * @param rect1 Rectangle to fit in rect2
   * @param rect2 target rectangle
   * @returns offset required to apply to rect1
   */
  private findRectangleOffset(rect1: DOMRect, rect2: DOMRect): { x: number; y: number } {
    let deltaX = 0;
    let deltaY = 0;

    // Calculate horizontal difference
    if (rect1.width > rect2.width) {
      // Center rect1 horizontally within rect2 if rect1 is wider
      deltaX = (rect2.x + (rect2.width / 2)) - (rect1.x + (rect1.width / 2));
    } else {
      // Move rect1 within the horizontal bounds of rect2
      if (rect1.x < rect2.x) {
        deltaX = rect2.x - rect1.x; // Align left edge
      } else if (rect1.right > rect2.right) {
        deltaX = rect2.right - rect1.right; // Align right edge
      }
    }

    // Calculate vertical difference
    if (rect1.height > rect2.height) {
      // Center rect1 vertically within rect2 if rect1 is taller
      deltaY = (rect2.y + (rect2.height / 2)) - (rect1.y + (rect1.height / 2));
    } else {
      // Move rect1 within the vertical bounds of rect2
      if (rect1.y < rect2.y) {
        deltaY = rect2.y - rect1.y; // Align top edge
      } else if (rect1.bottom > rect2.bottom) {
        deltaY = rect2.bottom - rect1.bottom; // Align bottom edge
      }
    }

    // Return the calculated differences
    return { x: deltaX, y: deltaY };
  }

  private resetDragRegion(): void {
    if (this.currentRegion) {
      this.currentRegion.setIsHoverTarget(false);
      this.currentRegion.removeOccupiedRegion(this);
      this.currentRegion = null;
    }
  }

  private resetPosition(): void {
    if (this.cloneParent || this.config.returnunregistered) {
      // clones always go back to their parents and don't call the layout group to animate
      this.doResetTween(this.initialFrame);
    } else if (this.onResetCallback) {
      this.onResetCallback();
    }
  }

  public doResetTween(pos: KHPoint): void {
    if (this.cloneParent) {
      gsap.to(this.dragElement, { left: pos.x, top: pos.y, duration: 0.25, ease: 'power2.inOut',
        onComplete: () => {
          this.dragElement.remove();
        } });
    } else {
      gsap.to(this.dragElement, { left: pos.x, top: pos.y, duration: 0.25, ease: 'power2.inOut' });
    }
  }

  public makeClone(force = false): ScorableDragRegion {
    if (!this.readyClone || force) {
      const cloneConfig = { ...this.config };
      cloneConfig.makeclones = false;
      cloneConfig.centerx = false;
      cloneConfig.centery = false;
      cloneConfig.frame = `${this.initialFrame.x} ${this.initialFrame.y} ${this.initialFrame.width} ${this.initialFrame.height}`;
      cloneConfig.visualElement = this.config.visualElement.cloneNode(true) as HTMLImageElement;
      const clone = new ScorableDragRegion(cloneConfig);
      clone.cloneParent = this;
      this.readyClone = clone;
    }

    return this.readyClone;
  }

  public wiggleAnimation(direction: -1 | 1): gsap.core.Timeline {
    const x = parseInt(this.dragElement.style.left);

    const sharedTweenProps = {
      duration: 0.2,
      left: `+=${10 * direction}`,
      ease: 'sine.inOut',
      repeat: 4,
      yoyo: true
    };

    const timeline = gsap.timeline();

    timeline.to(this.dragElement, { ...sharedTweenProps });
    if (this.readyClone) {
      timeline.to(this.readyClone.dragElement, { ...sharedTweenProps }, '<');
    }

    timeline.to(this.dragElement, { duration: 0.2, left: x, ease: 'sine.inOut' });
    if (this.readyClone) {
      timeline.to(this.readyClone.dragElement, { duration: 0.2, left: x, ease: 'sine.inOut' }, '<');
    }

    return timeline;
  }

  public getIsPositioned(): boolean {
    return this.isPositioned;
  }

  public setCanDrag(canDrag: boolean): void {
    if (!canDrag) {
      this.draggableMixin?.call(this);
      this.readyClone?.setCanDrag(false);
    } else {
      if (!this.draggableMixin) { // if drag isn't already setup, set it up
        this.setupDrag();
      }
    }
  }

  public isInRegion(): boolean {
    return this.currentRegion !== null;
  }

  public getAnswerValue(): string {
    return this.config.answervalue ? this.config.answervalue : this.config.key;
  }
}
